import React, { useState } from "react";
import { Route, Switch, useRouteMatch } from "react-router-dom";
import ProtectedRoute from "./ProtectedRoute";
import { useAuth } from "./contexts/AuthContext";
import { AdminSiteContext } from "./contexts/AdminSiteContext";

//MUI Components
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { CssBaseline } from "@material-ui/core";

//External MUI Components
import SideBar from "./components/SideBar"; //Drawer component
import TopBar from "./components/TopBar"; //AppBar component

//Page Components
import Login from "./pages/Login";
import Documentation from "./pages/Documentation";

import Pages from "./pages/Pages";
import PagesAdd from "./pages/PagesAdd";
import PagesEdit from "./pages/PagesEdit";

import Sites from "./pages/Sites";
import SitesAdd from "./pages/SitesAdd";
import SitesEdit from "./pages/SitesEdit";

import ExternalSites from "./pages/ExternalSites";
import ExternalSitesAdd from "./pages/ExternalSitesAdd";
import ExternalSitesEdit from "./pages/ExternalSitesEdit";

import Menus from "./pages/Menus";
import MenusEdit from "./pages/MenusEdit";

import Files from "./pages/Files";
import FilesAdd from "./pages/FilesAdd";
import FilesEdit from "./pages/FilesEdit";

import Units from "./pages/Units";
import UnitsAdd from "./pages/UnitsAdd";
import UnitsEdit from "./pages/UnitsEdit";

import People from "./pages/People";
import PeopleAdd from "./pages/PeopleAdd";
import PeopleEdit from "./pages/PeopleEdit";

import Entries from "./pages/Entries";
import EntriesAdd from "./pages/EntriesAdd";
import EntriesEdit from "./pages/EntriesEdit";

import Themes from "./pages/Themes";
import ThemesAdd from "./pages/ThemesAdd";
import ThemesEdit from "./pages/ThemesEdit";

import Regions from "./pages/Regions";
import RegionsAdd from "./pages/RegionsAdd";
import RegionsEdit from "./pages/RegionsEdit";

import Users from "./pages/Users";
import UsersAdd from "./pages/UsersAdd";
import UsersEdit from "./pages/UsersEdit";

import Profile from "./pages/Profile";

import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";

import NotFound from "./pages/NotFound";

const drawerWidth = 185;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
  },
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  appBarShift: {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  menuButton: {
    marginRight: 12,
  },
  hide: {
    display: "none",
  },
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: "nowrap",
  },
  drawerOpen: {
    width: drawerWidth,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  drawerClose: {
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: "hidden",
    width: theme.spacing(6) + 1,
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
  },
  content: {
    flexGrow: 1,
    padding: theme.spacing(3),
  },
}));

export default function App() {
  const classes = useStyles();
  const theme = useTheme();
  const { user } = useAuth();
  const [open, setOpen] = React.useState(true);
  let match = useRouteMatch();
  const [adminSelectedSite, setAdminSelectedSite] = useState();

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  return (
    <AdminSiteContext.Provider
      value={{ adminSelectedSite, setAdminSelectedSite }}
    >
      <div className={classes.root}>
        <CssBaseline />
        <TopBar drawerWidth={drawerWidth} open={open} setOpen={setOpen} />
        <SideBar
          classes={classes}
          theme={theme}
          handleDrawerClose={handleDrawerClose}
          handlerDrawerOpen={handleDrawerOpen}
          open={open}
        />
        <main className={classes.content}>
          <div className={classes.toolbar} />
          <Switch>
            <ProtectedRoute
              exact
              path={`${match.path}`}
              component={Documentation}
            />

            <ProtectedRoute
              exact
              path={`${match.path}/files`}
              component={Files}
            />
            <ProtectedRoute
              exact
              path={`${match.path}/files/add`}
              component={FilesAdd}
            />
            <ProtectedRoute
              exact
              path={`${match.path}/files/:id`}
              component={FilesEdit}
            />

            <ProtectedRoute
              exact
              path={`${match.path}/pages`}
              component={Pages}
            />
            <ProtectedRoute
              exact
              path={`${match.path}/pages/add`}
              component={PagesAdd}
            />
            <ProtectedRoute
              exact
              path={`${match.path}/pages/:id`}
              component={PagesEdit}
            />

            <ProtectedRoute
              exact
              path={`${match.path}/sites`}
              component={Sites}
            />
            <ProtectedRoute
              exact
              path={`${match.path}/sites/add`}
              component={SitesAdd}
            />
            <ProtectedRoute
              exact
              path={`${match.path}/sites/:id`}
              component={SitesEdit}
            />

            <ProtectedRoute
              exact
              path={`${match.path}/externalsites`}
              component={ExternalSites}
            />
            <ProtectedRoute
              exact
              path={`${match.path}/externalsites/add`}
              component={ExternalSitesAdd}
            />
            <ProtectedRoute
              exact
              path={`${match.path}/externalsites/:id`}
              component={ExternalSitesEdit}
            />

            <ProtectedRoute
              exact
              path={`${match.path}/menus`}
              component={Menus}
            />
            <ProtectedRoute
              exact
              path={`${match.path}/menus/:id`}
              component={MenusEdit}
            />

            <ProtectedRoute
              exact
              path={`${match.path}/units`}
              component={Units}
            />
            <ProtectedRoute
              exact
              path={`${match.path}/units/add`}
              component={UnitsAdd}
            />
            <ProtectedRoute
              exact
              path={`${match.path}/units/:id`}
              component={UnitsEdit}
            />

            <ProtectedRoute
              exact
              path={`${match.path}/people`}
              component={People}
            />
            <ProtectedRoute
              exact
              path={`${match.path}/people/add`}
              component={PeopleAdd}
            />
            <ProtectedRoute
              exact
              path={`${match.path}/people/:id`}
              component={PeopleEdit}
            />

            <ProtectedRoute
              exact
              path={`${match.path}/entries`}
              component={Entries}
            />
            <ProtectedRoute
              exact
              path={`${match.path}/entries/add`}
              component={EntriesAdd}
            />
            <ProtectedRoute
              exact
              path={`${match.path}/entries/:id`}
              component={EntriesEdit}
            />

            <ProtectedRoute
              exact
              path={`${match.path}/themes`}
              component={Themes}
            />
            <ProtectedRoute
              exact
              path={`${match.path}/themes/add`}
              component={ThemesAdd}
            />
            <ProtectedRoute
              exact
              path={`${match.path}/themes/:id`}
              component={ThemesEdit}
            />

            <ProtectedRoute
              exact
              path={`${match.path}/regions`}
              component={Regions}
            />
            <ProtectedRoute
              exact
              path={`${match.path}/regions/add`}
              component={RegionsAdd}
            />
            <ProtectedRoute
              exact
              path={`${match.path}/regions/:id`}
              component={RegionsEdit}
            />

            <ProtectedRoute
              exact
              path={`${match.path}/profile`}
              component={Profile}
            />
            <Route
              exact
              path={`${match.path}/forgot`}
              component={ForgotPassword}
            />
            <Route
              exact
              path={`${match.path}/reset/:token`}
              component={ResetPassword}
            />
            <Route exact path={`${match.path}/login`} component={Login} />

            {user.role === "admin" && (
              <Switch>
                <ProtectedRoute
                  exact
                  path={`${match.path}/users`}
                  component={Users}
                />
                <ProtectedRoute
                  exact
                  path={`${match.path}/users/add`}
                  component={UsersAdd}
                />
                <ProtectedRoute
                  exact
                  path={`${match.path}/users/:id`}
                  component={UsersEdit}
                />
                {/*Fallback Route*/}
                <Route component={NotFound} />
              </Switch>
            )}

            {/*Fallback Route*/}
            <Route component={NotFound} />
          </Switch>
        </main>
      </div>
    </AdminSiteContext.Provider>
  );
}
